import * as React from 'react';

import { cn } from '@/lib/utils';

import { BhanzuBuddyChatHeader } from './bhanzu-buddy-chat-header';
import { BhanzuBuddyChatInput } from './bhanzu-buddy-chat-input';
import { BhanzuBuddyChatMessageItem } from './bhanzu-buddy-chat-message-item';
import type { BhanzuBuddyChatProps } from './bhanzu-buddy-chat-types';
import { BhanzuBuddyEmptyState } from './bhanzu-buddy-empty-state';
import { injectLatexStyles } from './latex-styles';

// Inject LaTeX styles
injectLatexStyles();

/**
 * BhanzuBuddy Chat Component
 *
 * A complete chat interface with header, toggle button, chat area, and input field.
 * Matches the exact Figma MCP design specifications.
 *
 * Features:
 * - Header with title and Explain/Guide toggle
 * - Chat messages with buddy avatar and feedback
 * - User messages on the right
 * - Input field with voice and send buttons
 * - Loading states and animations
 */
const BhanzuBuddyChat = React.forwardRef<HTMLDivElement, BhanzuBuddyChatProps>(
  (
    {
      messages,
      onSendMessage,
      onFeedback,
      onGuideMe,
      onToggleMode,
      currentMode = 'explain',
      isLoading = false,
      className,
      ...props
    },
    ref
  ) => {
    const chatContainerRef = React.useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom whenever messages change
    React.useEffect(() => {
      if (chatContainerRef.current && messages.length > 0) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, [messages.length]);

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col bg-magical-moonlight-d4 rounded-2xl overflow-hidden',
          'w-full min-h-[840px] max-h-[80vh]',
          // Mobile responsiveness
          'max-w-full mx-auto',
          'sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl',
          className
        )}
        {...props}
      >
        {/* Header */}
        <BhanzuBuddyChatHeader
          onToggleMode={onToggleMode}
          currentMode={currentMode}
        />

        {/* Chat Messages Area */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6"
        >
          {messages.length === 0 ? (
            /* Encouraging Empty State */
            <BhanzuBuddyEmptyState currentMode={currentMode} />
          ) : (
            messages.map(message => (
              <BhanzuBuddyChatMessageItem
                key={message.id}
                message={message}
                onFeedback={onFeedback}
                onGuideMe={onGuideMe}
              />
            ))
          )}
        </div>

        {/* Input Area - Custom static input with send button functionality */}
        <BhanzuBuddyChatInput
          onSendMessage={onSendMessage ?? (() => {})}
          isLoading={isLoading}
          placeholder="Type here or talk..."
        />
      </div>
    );
  }
);

BhanzuBuddyChat.displayName = 'BhanzuBuddyChat';

// Memoize the component to prevent unnecessary re-renders
const BhanzuBuddyChatMemo = React.memo(BhanzuBuddyChat);

export { BhanzuBuddyChatMemo as BhanzuBuddyChat };
export type { BhanzuBuddyChatProps } from './bhanzu-buddy-chat-types';
